import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, SlidersHorizontal, X,
  ChevronLeft, ChevronRight, TrendingUp, ChevronRight as Arrow,
} from 'lucide-react';
import { depositsAPI } from '../../api/deposits';
import PageHeader  from '../../components/ui/PageHeader';
import StatusBadge from '../../components/ui/StatusBadge';

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const STATUSES = ['all', 'pending', 'approved', 'rejected'];
const PER_PAGE = 15;

// ── Skeleton ─────────────────────────────────────────────────
function DepositSkeleton() {
  return (
    <div className="card flex items-center gap-3">
      <div className="skeleton h-10 w-10 flex-shrink-0 rounded-full" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="skeleton h-3.5 w-28 rounded" />
        <div className="skeleton h-3 w-20 rounded" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="skeleton h-4 w-24 rounded" />
        <div className="skeleton h-3 w-16 rounded" />
      </div>
    </div>
  );
}

// ── Deposit card ─────────────────────────────────────────────
function DepositCard({ deposit, onClick }) {
  const initials = deposit.user.name
    .split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  return (
    <button
      onClick={onClick}
      className="card w-full text-left flex items-center gap-3
                 transition-all duration-200 hover:border-gold/40 active:scale-[0.98]"
    >
      {/* Avatar */}
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center
                      rounded-full text-xs font-bold text-white"
           style={{ background: 'linear-gradient(135deg,#1448c4 0%,#0a2260 100%)' }}>
        {initials}
      </div>

      {/* User info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{deposit.user.name}</p>
        <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {deposit.user.mobile}
        </p>
      </div>

      {/* Right: amount + status + date */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        <p className="text-sm font-bold text-gold-shimmer">
          {formatINR(deposit.amount)}
        </p>
        <StatusBadge status={deposit.status} />
        <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
          {formatDate(deposit.deposited_at)}
        </p>
      </div>
    </button>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function DepositsPage() {
  const navigate = useNavigate();

  // filters
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState('all');
  const [from,        setFrom]        = useState('');
  const [to,          setTo]          = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // data
  const [deposits,   setDeposits]   = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // debounce search
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 450);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  // reset page on filter change
  useEffect(() => { setPage(1); }, [status, from, to]);

  // ── Fetch ────────────────────────────────────────────────
  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: PER_PAGE };
      if (debouncedSearch)  params.search = debouncedSearch;
      if (status !== 'all') params.status = status;
      if (from)             params.from   = from;
      if (to)               params.to     = to;

      const { data } = await depositsAPI.getList(params);
      if (data.status) {
        setDeposits(data.data.deposits);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load deposits.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, from, to, page]);

  useEffect(() => { fetchDeposits(); }, [fetchDeposits]);

  const clearFilters = () => { setStatus('all'); setFrom(''); setTo(''); setPage(1); };
  const hasActiveFilters = status !== 'all' || from || to;

  // status tab colour
  const statusTabStyle = (s) => ({
    background: status === s
      ? s === 'pending'  ? 'rgba(245,158,11,0.3)'
      : s === 'approved' ? 'rgba(5,150,105,0.3)'
      : s === 'rejected' ? 'rgba(239,68,68,0.3)'
      : 'linear-gradient(135deg,#d4a017,#f0c040)'
      : 'rgba(255,255,255,0.07)',
    color: status === s
      ? s === 'pending'  ? '#fcd34d'
      : s === 'approved' ? '#6ee7b7'
      : s === 'rejected' ? '#fca5a5'
      : '#fff'
      : 'rgba(255,255,255,0.5)',
    border: status === s ? 'none' : '1px solid rgba(255,255,255,0.1)',
  });

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader
        title="Deposits"
        right={
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="relative flex h-8 w-8 items-center justify-center rounded-xl transition-all active:scale-90"
            style={{
              background: showFilters ? 'rgba(212,160,23,0.2)' : 'rgba(255,255,255,0.08)',
              border: showFilters
                ? '1px solid rgba(212,160,23,0.4)'
                : '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <SlidersHorizontal size={15}
              style={{ color: showFilters ? '#d4a017' : 'rgba(255,255,255,0.6)' }} />
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full"
                    style={{ background: '#d4a017' }} />
            )}
          </button>
        }
      />

      <div className="flex flex-col gap-3 p-4">

        {/* ── Search ── */}
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'rgba(255,255,255,0.35)' }} />
          <input
            type="text"
            placeholder="Search by user name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-9 pr-9"
          />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(255,255,255,0.4)' }}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div className="card flex flex-col gap-4 animate-fade-in">

            {/* Status tabs */}
            <div>
              <p className="input-label mb-2">Status</p>
              <div className="flex gap-2 flex-wrap">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className="rounded-xl px-3 py-1.5 text-xs font-semibold capitalize
                               transition-all duration-150"
                    style={statusTabStyle(s)}
                  >
                    {s === 'all' ? 'All' : s}
                  </button>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <label className="input-label">From</label>
                <input type="date" value={from}
                       onChange={(e) => setFrom(e.target.value)}
                       className="input-field" />
              </div>
              <div className="flex flex-col">
                <label className="input-label">To</label>
                <input type="date" value={to}
                       onChange={(e) => setTo(e.target.value)}
                       className="input-field" />
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters}
                      className="flex items-center gap-1.5 self-start text-xs font-medium
                                 transition-opacity hover:opacity-70"
                      style={{ color: '#fca5a5' }}>
                <X size={12} /> Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Active status pill (quick view when filter closed) ── */}
        {!showFilters && status !== 'all' && (
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>Filtered:</span>
            <StatusBadge status={status} />
            <button onClick={clearFilters}
                    className="ml-auto text-xs"
                    style={{ color: '#fca5a5' }}>
              <X size={12} />
            </button>
          </div>
        )}

        {/* ── Summary ── */}
        {pagination && !loading && (
          <p className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Showing {deposits.length} of {pagination.total} deposit{pagination.total !== 1 ? 's' : ''}
          </p>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="card py-6 text-sm text-center" style={{ color: '#fca5a5' }}>
            {error}
          </div>
        )}

        {/* ── Loading skeletons ── */}
        {loading && !error && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 6 }).map((_, i) => <DepositSkeleton key={i} />)}
          </div>
        )}

        {/* ── Deposit list ── */}
        {!loading && !error && deposits.length > 0 && (
          <div className="flex flex-col gap-3">
            {deposits.map((deposit) => (
              <DepositCard
                key={deposit.id}
                deposit={deposit}
                onClick={() => navigate(`/deposits/${deposit.id}`)}
              />
            ))}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && deposits.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(255,255,255,0.06)' }}>
              <TrendingUp size={24} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {debouncedSearch || hasActiveFilters
                ? 'No deposits match your filters'
                : 'No deposits found'}
            </p>
            {(debouncedSearch || hasActiveFilters) && (
              <button
                onClick={() => { setSearch(''); clearFilters(); }}
                className="text-xs font-medium"
                style={{ color: '#d4a017' }}
              >
                Clear all filters
              </button>
            )}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              <ChevronLeft size={15} /> Prev
            </button>

            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Page {pagination.current_page} / {pagination.last_page}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
              disabled={page === pagination.last_page}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium
                         transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)' }}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
